import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, ArrowRight, Loader2, Building2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API } from "@/App";
import { authHeaders } from "@/lib/firebase";
import BrandMark from "@/components/BrandMark";

const BUSINESS_TYPES = ["Gym", "Yoga / Pilates studio", "Fitness studio", "Sports club",
  "Wellness / Spa", "Retreat", "Coaching", "Dance studio", "Other"];

const OnboardingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(searchParams.get("step") === "plan" ? 2 : 1);
  const [plans, setPlans] = useState([]);
  const [interval, setInterval] = useState("monthly");
  const [form, setForm] = useState({ business_name: "", business_type: "", city: "" });
  const [saving, setSaving] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/host/account`, { headers: await authHeaders() });
        const data = await res.json();
        setPlans(data.plans || []);
        if (data.active) { navigate("/host", { replace: true }); return; }
        if (data.business) {
          setForm({
            business_name: data.business.name || "",
            business_type: data.business.type || "",
            city: data.business.city || "",
          });
        }
        if (searchParams.get("step") === "plan" || data.onboarding_complete) setStep(2);
      } catch {
        toast.error("Couldn't load your account.");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, searchParams]);

  const saveInfo = async (e) => {
    e.preventDefault();
    if (!form.business_name.trim()) { toast.error("Please enter your business name"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/host/account/onboarding`, {
        method: "POST",
        headers: { ...(await authHeaders()), "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setStep(2);
    } catch {
      toast.error("Couldn't save. Please try again.");
    } finally { setSaving(false); }
  };

  const subscribe = async (planId) => {
    setCheckoutPlan(planId);
    try {
      const res = await fetch(`${API}/host/subscription/checkout`, {
        method: "POST",
        headers: { ...(await authHeaders()), "Content-Type": "application/json" },
        body: JSON.stringify({ plan: planId, interval, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok || !data.checkout_url) throw new Error(data.detail || "Checkout failed");
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.message || "Couldn't start checkout.");
      setCheckoutPlan(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f1e8]">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f1e8] text-zinc-900" data-testid="onboarding-page">
      <header className="flex items-center justify-between px-6 sm:px-10 py-5">
        <div className="flex items-center gap-2">
          <BrandMark />
        </div>
        <div className="flex items-center gap-2 text-sm text-zinc-500" data-testid="onboarding-steps">
          <span className={step === 1 ? "font-semibold text-zinc-900" : ""}>1. Business</span>
          <span>→</span>
          <span className={step === 2 ? "font-semibold text-zinc-900" : ""}>2. Plan</span>
        </div>
      </header>

      {step === 1 && (
        <div className="max-w-md mx-auto px-6 pt-10 sm:pt-16" data-testid="onboarding-step-business">
          <div className="w-12 h-12 rounded-2xl bg-zinc-900 text-[#d4ff4f] flex items-center justify-center mb-5">
            <Building2 className="w-6 h-6" />
          </div>
          <h1 className="font-display text-3xl font-bold">Tell us about your business</h1>
          <p className="text-zinc-600 mt-2 text-sm">This sets up your Host Hub. You can change it later in Settings.</p>
          <form onSubmit={saveInfo} className="mt-7 space-y-4">
            <div>
              <Label htmlFor="bn">Business name *</Label>
              <Input id="bn" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                placeholder="e.g. Flow Yoga Studio" className="mt-1 bg-white" data-testid="onboarding-business-name" />
            </div>
            <div>
              <Label>Business type</Label>
              <Select value={form.business_type} onValueChange={(v) => setForm({ ...form, business_type: v })}>
                <SelectTrigger className="mt-1 bg-white" data-testid="onboarding-business-type"><SelectValue placeholder="Choose a type" /></SelectTrigger>
                <SelectContent>
                  {BUSINESS_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="city">City</Label>
              <Input id="city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                placeholder="e.g. London" className="mt-1 bg-white" data-testid="onboarding-city" />
            </div>
            <Button type="submit" disabled={saving} className="w-full rounded-full gap-2" data-testid="onboarding-continue">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4" /></>}
            </Button>
          </form>
        </div>
      )}

      {step === 2 && (
        <div className="max-w-3xl mx-auto px-6 pt-8 sm:pt-12 pb-20" data-testid="onboarding-step-plan">
          <button onClick={() => setStep(1)} className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-4" data-testid="onboarding-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <h1 className="font-display text-3xl font-bold text-center">Choose your plan</h1>
          <p className="text-zinc-600 mt-2 text-sm text-center">Subscribe to activate your Host Hub. Cancel anytime.</p>

          <div className="flex items-center justify-center mt-6">
            <div className="inline-flex rounded-full bg-white border border-zinc-200 p-1" data-testid="onboarding-interval-toggle">
              {["monthly", "yearly"].map((it) => (
                <button key={it} onClick={() => setInterval(it)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize transition-colors ${interval === it ? "bg-zinc-900 text-white" : "text-zinc-600"}`}
                  data-testid={`onboarding-interval-${it}`}>
                  {it}{it === "yearly" && <span className="ml-1 text-[#8bba00]">·save</span>}
                </button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 mt-7">
            {plans.map((p) => (
              <div key={p.id}
                className={`rounded-3xl p-7 bg-white border ${p.highlight ? "border-zinc-900 ring-1 ring-zinc-900" : "border-zinc-200"}`}
                data-testid={`onboarding-plan-${p.id}`}>
                {p.highlight && <span className="inline-block px-2.5 py-0.5 rounded-full bg-[#d4ff4f] text-zinc-900 text-xs font-semibold mb-3">Most popular</span>}
                <h3 className="font-display text-2xl font-bold">{p.name}</h3>
                <p className="text-sm text-zinc-500 mt-1">{p.tagline}</p>
                <div className="mt-5 flex items-end gap-1">
                  <span className="font-display text-4xl font-bold">${interval === "monthly" ? p.monthly : p.yearly_monthly_equiv}</span>
                  <span className="text-zinc-500 mb-1 text-sm">/mo</span>
                </div>
                <p className="text-xs text-zinc-400 mt-1">{interval === "yearly" ? `$${p.yearly} billed yearly` : "billed monthly"}</p>
                {p.trial_days > 0 && (
                  <p className="text-xs font-medium text-[#6a9400] mt-1" data-testid={`onboarding-trial-${p.id}`}>
                    {p.trial_days}-day free trial · cancel anytime
                  </p>
                )}
                <ul className="mt-6 space-y-2.5">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-700">
                      <Check className="w-4 h-4 text-[#8bba00] mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Button onClick={() => subscribe(p.id)} disabled={!!checkoutPlan}
                  className={`w-full mt-7 rounded-full gap-2 ${p.highlight ? "" : "bg-zinc-900 hover:bg-zinc-800"}`}
                  data-testid={`onboarding-subscribe-${p.id}`}>
                  {checkoutPlan === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{p.trial_days > 0 ? `Start ${p.trial_days}-day free trial` : `Subscribe to ${p.name}`}</>}
                </Button>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-zinc-400 mt-6">Secure checkout by Stripe. You'll be redirected to pay.</p>
        </div>
      )}
    </div>
  );
};

export default OnboardingPage;
